import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  Alert,
  IconButton,
} from '@mui/material';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import CustomButton from '../../MUI_custom_component/Button/CustomButton';
import ReusableModal from '../../MUI_custom_component/Modal/CustomModal';
import { toast } from 'react-toastify';
import AddressFormModal from './AddressFormModal';
import { reverseGeocodeAction } from '../APIs/action';
import { mapStyles } from '../mapStyles';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import Entity from '../../../v3/Common/Entity';
import { useSelector } from 'react-redux';

class GoogleMapsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {}

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant='body1'
            color='text.secondary'
          >
            Map temporarily unavailable
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            Please refresh the page
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

const LocationMapSelector = ({
  mapCenter,
  markerPosition,
  onMapClick,
  onCurrentLocation,
  onConfirmLocation,
  onCloseModal,
  preloadedAddressData = null,
  handleSaveAddress = () => {},
  zoom = 15,
  isPwa = false,
  non_reward = false,
}) => {
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const [map, setMap] = useState(null);
  const [mapError, setMapError] = useState(null);
  const [locationPermissionDenied, setLocationPermissionDenied] =
    useState(false);
  const [addressData, setAddressData] = useState(null);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [addressFormModalOpen, setAddressFormModalOpen] = useState(false);
  const usedSapCodes = useSelector(state => state.AddressReducer?.usedSapCodes || []);
  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback((map) => {
    if (
      typeof window !== 'undefined' &&
      window.google &&
      window.google.maps &&
      map
    ) {
      try {
        setMap(null);
      } catch (error) {
        setMap(null);
      }
    } else {
      setMap(null);
    }
  }, []);

  const fetchAddressFromCoordinates = useCallback(
    async (lat, lng) => {
      if (
        preloadedAddressData &&
        markerPosition &&
        Array.isArray(markerPosition) &&
        markerPosition.length >= 2 &&
        Math.abs(markerPosition[0] - lat) < 0.0001 &&
        Math.abs(markerPosition[1] - lng) < 0.0001
      ) {
        setAddressData(preloadedAddressData);
        return;
      }

      setLoadingAddress(true);
      const response = await reverseGeocodeAction(lat, lng);
      setLoadingAddress(false);
      if (!response.error && response.data) {
        setAddressData(response.data);
      } else {
        setAddressFormModalOpen(true);
      }
    },
    [preloadedAddressData, markerPosition],
  );

  const handleLoadError = useCallback(() => {
    setMapError(
      'Failed to load Google Maps. Please check your internet connection and try again.',
    );
  }, []);

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    onMapClick({ latlng: { lat, lng } });

    fetchAddressFromCoordinates(lat, lng);
  };

  const handleAddressFormModalClose = () => {
    setAddressFormModalOpen(false);
  };

  const handleAddressFormBack = () => {
    setAddressFormModalOpen(false);
  };

  const handleAddressSave = (addressData) => {
    setAddressFormModalOpen(false);
    handleSaveAddress();
    if (onConfirmLocation) {
      onConfirmLocation(addressData);
    }

    // Close the location map selector after address is saved
    if (onCloseModal) {
      onCloseModal();
    }
  };

  const handleLocationSubmit = () => {
    // Open the address form modal
    setAddressFormModalOpen(true);
  };

  const handleCurrentLocationRequest = () => {
    getCurrentLocation(false);
  };

  const getCurrentLocation = (isRetry = false) => {
    if (!navigator.geolocation) {
      if (!isRetry) {
        setMapError('Geolocation is not supported by this browser.');
      }
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 15000, // Increased timeout for better reliability
      maximumAge: 0, // Always request fresh location
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const location = {
          latlng: {
            lat: latitude,
            lng: longitude,
          },
        };

        onCurrentLocation(location);
        setLocationPermissionDenied(false);
        setMapError(null); // Clear any previous errors

        fetchAddressFromCoordinates(latitude, longitude);
      },
      (error) => {
        if (isRetry) {
          // For retry scenarios, only show browser settings tip for permission denied
          if (error.code === error.PERMISSION_DENIED) {
            setLocationPermissionDenied(true);
            toast.info('Tip: Look for the location icon in your browser\'s address bar to manage location permissions.');
          } else {
            setLocationPermissionDenied(false);
          }
        } else {
          // For initial request, show detailed error messages
          let errorMessage = 'Unable to retrieve your location. ';

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access was denied. Please enable location permission in your browser settings and try again.';
              setLocationPermissionDenied(true);
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please check your GPS settings and try again.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please check your internet connection and try again.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting your location. Please try again.';
              break;
          }

          setMapError(errorMessage);
        }
      },
      options,
    );
  };

  const handleRetryLocation = () => {
    setLocationPermissionDenied(false);
    setMapError(null);
    // Request location permission again (with retry flag)
    getCurrentLocation(true);
  };

  useEffect(() => {
    if (
      markerPosition &&
      Array.isArray(markerPosition) &&
      markerPosition.length >= 2 &&
      markerPosition[0] !== 28.7041 && // Don't fetch address for default coordinates
      markerPosition[1] !== 77.1025
    ) {
      fetchAddressFromCoordinates(markerPosition[0], markerPosition[1]);
    }
  }, [markerPosition, fetchAddressFromCoordinates]);

  useEffect(() => {
    if (preloadedAddressData) {
      setAddressData(preloadedAddressData);
    }
  }, [preloadedAddressData]);

  // Check if required props are not provided or use default coordinates for display
  const shouldShowLoadingState = false; // Always show the map with default or provided coordinates
  
  // Use default coordinates for display when no real coordinates provided
  const displayMapCenter = mapCenter && Array.isArray(mapCenter) && mapCenter.length >= 2 
    ? mapCenter 
    : [28.7041, 77.1025]; // Default to Delhi
    
  const displayMarkerPosition = markerPosition && Array.isArray(markerPosition) && markerPosition.length >= 2 
    ? markerPosition 
    : [28.7041, 77.1025]; // Default to Delhi

  if (shouldShowLoadingState) {
    return (
      <Box
        sx={{
          height: isMobile ? '100vh' : '589px',
          width: isMobile ? '100vh' : '618px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Typography
          variant='body1'
          color='text.secondary'
        >
          Map data is loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: isMobile ? '100vh' : '589px',
        width: isMobile ? '100%' : '618px',
        position: isMobile ? 'relative' : 'fixed',
        // top: isMobile ? 'auto' : '50%',
        // left: isMobile ? 'auto' : '50%',
        // transform: isMobile ? 'none' : 'translate(-50%, -50%)',
        borderRadius: isMobile ? 0 : '12px',
        overflow: 'hidden',
        // zIndex: isMobile ? 'auto' : 1300,
        // boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.24)',
        // Hide LocationMapSelector when AddressFormModal is open on desktop
        // display: !isMobile && addressFormModalOpen ? 'none' : 'flex',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',

      }}
    >
      <Box
          sx={{
            position: 'absolute',
            top: 16,
            [isMobile ? 'left' : 'right']: 16,
            zIndex: 1002,
          }}
        >
          <IconButton
            onClick={onCloseModal}
            sx={{
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              width: isMobile ? 40 : 36,
              height: isMobile ? 40 : 36,
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            {isMobile ? (
              <ArrowBackIcon sx={{ fontSize: 24, color: '#333' }} />
            ) : (
              <CloseIcon sx={{ fontSize: 24, color: '#333' }} />
            )}
          </IconButton>
      </Box>
      <Box sx={{ position: 'relative', flex: 1 }}>
        {/* Close/Back Button */}
       

        {mapError ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Alert
              severity='error'
              sx={{ maxWidth: '400px' }}
            >
              {mapError}
            </Alert>
          </Box>
        ) : (
          <LoadScript
            googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
            onError={handleLoadError}
            loadingElement={
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography>Loading map...</Typography>
              </Box>
            }
          >
            <GoogleMapsErrorBoundary>
              <GoogleMap
                key='location-map-selector'
                mapContainerStyle={{
                  height: '100%',
                  width: '100%',
                }}
                center={{
                  lat: displayMapCenter[0],
                  lng: displayMapCenter[1],
                }}
                zoom={zoom}
                onLoad={onLoad}
                onUnmount={onUnmount}
                onClick={handleMapClick}
                options={{
                  styles: mapStyles,
                  disableDefaultUI: true,
                  zoomControl: true,
                  zoomControlOptions: {
                    position: 9,
                  },
                }}
              >
                <Marker
                  position={{
                    lat: displayMarkerPosition[0],
                    lng: displayMarkerPosition[1],
                  }}
                  icon={
                    window.google && window.google.maps && window.google.maps.Size && window.google.maps.Point
                      ? {
                          url:
                            'data:image/svg+xml;charset=UTF-8,' +
                            encodeURIComponent(`
                            <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
                              <circle cx="60" cy="60" r="50" fill="#995A0012" stroke="#995A00" stroke-width="1"/>
                              <path d="M60 40c-5.52 0-10 4.48-10 10 0 7.5 10 20 10 20s10-12.5 10-20c0-5.52-4.48-10-10-10zm0 13.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" fill="#333333"/>
                            </svg>
                          `),
                          scaledSize: new window.google.maps.Size(120, 120),
                          anchor: new window.google.maps.Point(60, 75),
                        }
                      : {
                          url:
                            'data:image/svg+xml;charset=UTF-8,' +
                            encodeURIComponent(`
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                              <circle cx="20" cy="20" r="16" fill="#995A0012" stroke="#995A00" stroke-width="1"/>
                              <path d="M20 13c-1.84 0-3.33 1.49-3.33 3.33 0 2.5 3.33 6.67 3.33 6.67s3.33-4.17 3.33-6.67c0-1.84-1.49-3.33-3.33-3.33zm0 4.5c-0.64 0-1.17-0.52-1.17-1.17s0.52-1.17 1.17-1.17 1.17 0.52 1.17 1.17-0.52 1.17-1.17 1.17z" fill="#333333"/>
                            </svg>
                          `),
                        }
                  }
                />
              </GoogleMap>
            </GoogleMapsErrorBoundary>
          </LoadScript>
        )}
        
    </Box>
      {!mapError && (
        <>
          
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'white',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              borderBottomLeftRadius: isMobile ? 0 : 12,
              borderBottomRightRadius: isMobile ? 0 : 12,
              padding: isMobile ? '16px' : '20px',
              boxShadow: isMobile ? '0 4px 20px rgba(0, 0, 0, 0.15)' : 'none',
              // zIndex: 1000,
              height: non_reward ? '188px' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            
            <Box>
              {!non_reward && (
                <Entity
                  gridSizes={{
                    entity: { xs: 12, sm: 12, md: 6 } 
                  }}
                  showManufacturerSelect={false}
                  forceRefresh={false}
                  showEntitySelect={true}
                  autoFetch={false}
                  moduleSpecificSapCodes={usedSapCodes}
                  enableSapCodeFiltering={true}
                />
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                }}
              >
                <PlaceOutlinedIcon
                  sx={{ height: '16px', width: '16px', marginRight: '2px' }}
                  color='#666666'
                />
                <Typography variant='h2' sx={{ marginTop: '4px' }}>
                  {addressData?.landmark}
                </Typography>
              </Box>

              {loadingAddress ? (
                <Typography
                  variant='body1'
                  sx={{ fontStyle: 'italic', color: '#666' }}
                >
                  Fetching address...
                </Typography>
              ) : addressData ? (
                <Box>
                  <Typography
                    variant='body1'
                    sx={{ marginTop: '4px' }}
                  >
                    {addressData.formatted_address}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant='body1'
                  sx={{ fontStyle: 'italic', color: '#666' }}
                >
                  Select a location on the map to see address details
                </Typography>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CustomButton
                variant='filled'
                color='warning'
                label='Confirm Location'
                fullWidth
                onClick={handleLocationSubmit}
              />
            </Box>
          </Box>
        </>
      )}

      <ReusableModal
        open={addressFormModalOpen}
        onClose={handleAddressFormModalClose}
        centerAlign={true}
        maxHeight={isMobile ? '100vh' : '589px'}
        topDesktop={'50%'}
        topMobile={'0%'}
        showHeader={false}
        scrollable={false}
        pt={0}
        px={0}
        pb={0}
        maxWidth={isMobile ? '100%' : '618px'}
        hideBackdrop={true}
      >
        <AddressFormModal
          onBack={handleAddressFormBack}
          onSaveAddress={handleAddressSave}
          addressDetails={addressData}
          locationPermissionDenied={locationPermissionDenied}
          handleRetryLocation={handleRetryLocation}
          handleCurrentLocationRequest={handleCurrentLocationRequest}
          non_reward={non_reward}
          coords={
            markerPosition && Array.isArray(markerPosition) && markerPosition[0] !== 28.7041 && markerPosition[1] !== 77.1025
              ? { lat: markerPosition[0], lng: markerPosition[1] }
              : null // Don't pass default coordinates
          }
          moduleSpecificSapCodes={usedSapCodes}
        />
      </ReusableModal>
    </Box>
  );
};

export default LocationMapSelector;
